import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "react-bootstrap";
import { AnimatePresence, motion } from "framer-motion";
import { getLanguageColor } from "../ObjectFunctions/languageColors";

export const CardIcons = (props) => {
	const languages = useMemo(
		() => (Array.isArray(props.languages) ? props.languages : []),
		[props.languages]
	);
	const isLoading = Boolean(props.isLoading);
	const viewportRef = useRef(null);
	const trackRef = useRef(null);
	const [scrollDistance, setScrollDistance] = useState(0);

	const containerVariants = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,
			transition: {
				staggerChildren: 0.06,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 6, scale: 0.96 },
		show: {
			opacity: 1,
			y: 0,
			scale: 1,
			transition: {
				duration: 0.2,
				ease: [0.22, 1, 0.36, 1],
			},
		},
	};

	useEffect(() => {
		if (isLoading || languages.length === 0) {
			setScrollDistance(0);
			return;
		}

		const updateDistance = () => {
			if (!viewportRef.current || !trackRef.current) {
				setScrollDistance(0);
				return;
			}

			const overflow =
				trackRef.current.scrollWidth - viewportRef.current.clientWidth;
			setScrollDistance(overflow > 0 ? overflow : 0);
		};

		updateDistance();
		window.addEventListener("resize", updateDistance);

		return () => {
			window.removeEventListener("resize", updateDistance);
		};
	}, [isLoading, languages]);

	const languageScrollTransition =
		scrollDistance > 0
			? {
					duration: Math.max(8, scrollDistance / 18),
					repeat: Infinity,
					repeatType: "reverse",
					ease: "easeInOut",
			  }
			: { duration: 0.25 };

	return (
		<motion.div
			layout
			className="d-flex align-items-center"
			variants={containerVariants}
			initial={false}
			animate="show"
		>
			<AnimatePresence mode="wait" initial={false}>
				{isLoading && (
					<motion.span
						key="loading-languages"
						className="language-marquee-viewport"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						<div className="language-marquee-track">
							<span className="language-pill-skeleton"></span>
							<span className="language-pill-skeleton"></span>
							<span className="language-pill-skeleton"></span>
							<span className="language-pill-skeleton"></span>
						</div>
					</motion.span>
				)}
				{!isLoading && languages.length === 0 && (
					<motion.span
						key="no-language-data"
						className="subfont text-muted"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						No language data available.
					</motion.span>
				)}
				{!isLoading &&
					languages.length > 0 && (
						<motion.div
							key="languages-track"
							ref={viewportRef}
							className="language-marquee-viewport"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						>
							<motion.div
								ref={trackRef}
								className="language-marquee-track"
								animate={scrollDistance > 0 ? { x: [0, -scrollDistance] } : { x: 0 }}
								transition={languageScrollTransition}
							>
								{languages.map((language, index) => (
									<motion.span
										key={`${language}-${index}`}
										layout
										variants={itemVariants}
										initial="hidden"
										animate="show"
										exit="hidden"
									>
										<Badge
											pill
											className="subfont"
											style={{
												backgroundColor: getLanguageColor(language),
												color: "#fff",
												fontSize: "0.85rem",
												fontWeight: 600,
												padding: "0.45rem 0.7rem",
											}}
										>
											{language}
										</Badge>
									</motion.span>
								))}
							</motion.div>
						</motion.div>
					)}
			</AnimatePresence>
		</motion.div>
	);
};
